import React from 'react';
import Stack from '@mui/material/Stack';
import { Button } from '@mui/material';

import CustomizableCard from 'common/template-parts/CustomizableCard';
import AuthorizationSequencingCardContent from 'feature/loading-management/authorization-sequencing/templates/AuthorizationSequencingCardContent';
import LoadableChip from 'feature/loading-management/authorization-sequencing/templates/LoadableChip';

const CardFooter = () => {
  return (
    <>
      <Button color="primary" onClick={() => {}} sx={{ cursor: 'pointer' }} variant="outlined">
        Save Sequence
      </Button>
      <Button color="primary" onClick={() => {}} sx={{ cursor: 'pointer' }} variant="outlined">
        Cancel
      </Button>
    </>
  );
};

const FilterableCardsPreview = () => {
  const baseAuthSequence = {
    command: {
      authorizationSequence: [271034, 266976],
      futureSequence: null,
      loadingAllowed: true,
      locationId: 227,
      productId: 114,
      supplier: {
        companyCustomerIdName: '410',
        dataCompanyId: 1,
      },
    },
    dataCompany: 'MPL',
    location: {
      id: 227,
      nameLong: '227 CARTHAGE',
      nameShort: '227 CARTHAGE',
    },
    product: {
      id: 114,
      nameLong: 'E DENATURED FUEL',
      nameShort: 'E DENATURED FUEL',
      proprietary: null,
      type: 'Base',
    },
    sequencedAuthorizations: [
      {
        active: false,
        futureSequenceDate: null,
        futureSequenceNumber: null,
        inventoryOwner: {
          companyCustomerIdName: '357',
          dataCompanyId: 1,
          nameLong: '357 BIOURJA TRADING LLC',
          nameShort: '357 BIOURJA',
        },
        productAuthorizationId: 271034,
        sequenceNumber: 1,
        supplierDirectedBlend: false,
      },
      {
        active: true,
        futureSequenceDate: null,
        futureSequenceNumber: null,
        inventoryOwner: {
          companyCustomerIdName: '25',
          dataCompanyId: 1,
          nameLong: '25 HTP ENERGY',
          nameShort: '25 HTP ENERGY',
        },
        productAuthorizationId: 266976,
        sequenceNumber: 2,
        supplierDirectedBlend: false,
      },
    ],
    supplier: {
      companyCustomerIdName: '410',
      dataCompanyId: 1,
      nameLong: '410 PHILLIPS 66',
      nameShort: '410 PHILLIPS66',
    },
    unsequencedAuthorizations: [
      {
        active: true,
        futureSequenceDate: null,
        futureSequenceNumber: null,
        inventoryOwner: {
          companyCustomerIdName: '326',
          dataCompanyId: 1,
          nameLong: '326 MINNIOWA DISTRIBUTING, INC',
          nameShort: '326 MINNIOWA DIST',
        },
        productAuthorizationId: 482610,
        sequenceNumber: null,
        supplierDirectedBlend: false,
      },
    ],
  };

  const authSequenceVariants = [
    {
      authSequenceData: {
        ...baseAuthSequence,
        loadingAllowed: true,
      },
      id: 'mixed-sequenced-and-unsequenced',
      isLoadable: true,
    },
    {
      authSequenceData: {
        ...baseAuthSequence,
        loadingAllowed: true,
        product: {
          ...baseAuthSequence.product,
          id: 222,
          nameLong: 'REFORMULATED GASOLINE',
          nameShort: 'RFG',
        },
        unsequencedAuthorizations: [],
      },
      id: 'only-sequenced',
      isLoadable: true,
    },
    {
      authSequenceData: {
        ...baseAuthSequence,
        loadingAllowed: false,
        product: {
          ...baseAuthSequence.product,
          id: 333,
          nameLong: 'BIODIESEL B20',
          nameShort: 'BIODIESEL B20',
        },
        sequencedAuthorizations: [],
        unsequencedAuthorizations: [
          {
            active: false,
            futureSequenceDate: null,
            futureSequenceNumber: null,
            inventoryOwner: {
              companyCustomerIdName: '500',
              dataCompanyId: 1,
              nameLong: '500 HORIZON ENERGY PARTNERS',
              nameShort: '500 HORIZON',
            },
            productAuthorizationId: 900001,
            sequenceNumber: null,
            supplierDirectedBlend: false,
          },
          {
            active: true,
            futureSequenceDate: null,
            futureSequenceNumber: null,
            inventoryOwner: {
              companyCustomerIdName: '501',
              dataCompanyId: 1,
              nameLong: '501 BLUE SKY FUELS',
              nameShort: '501 BLUE SKY',
            },
            productAuthorizationId: 900002,
            sequenceNumber: null,
            supplierDirectedBlend: false,
          },
        ],
      },
      id: 'only-unsequenced',
      isLoadable: false,
    },
    {
      authSequenceData: {
        ...baseAuthSequence,
        loadingAllowed: false,
        product: {
          ...baseAuthSequence.product,
          id: 444,
          nameLong: 'JET FUEL',
          nameShort: 'JET FUEL',
        },
        sequencedAuthorizations: [],
        unsequencedAuthorizations: [],
      },
      id: 'no-authorizations',
      isLoadable: false,
    },
  ];

  // Transform the data to match what CustomizableCard expects
  const transformAuthSequenceData = data => ({
    loadingAllowed: data.command.loadingAllowed,
    locationId: data.location.id,
    nameLong: `${data.product.nameLong} - ${data.location.nameLong}`,
    notSequenced: data.unsequencedAuthorizations.map(auth => ({
      inventoryOwner: auth.inventoryOwner.nameShort,
      isActive: auth.active,
      productAuthorizationId: auth.productAuthorizationId,
      sequenceNumber: auth.sequenceNumber,
      supplierDirectedBlend: auth.supplierDirectedBlend,
    })),
    product: data.product,
    productId: data.product.id,
    sequenced: data.sequencedAuthorizations.map(auth => ({
      inventoryOwner: auth.inventoryOwner.nameShort,
      isActive: auth.active,
      productAuthorizationId: auth.productAuthorizationId,
      sequenceNumber: auth.sequenceNumber,
      supplierDirectedBlend: auth.supplierDirectedBlend,
    })),
  });

  return (
    <>
      <Stack alignItems="center" gap={2} sx={{ marginTop: 6, padding: 2 }}>
        {authSequenceVariants.map(variant => (
          <CustomizableCard
            cardContent={
              <AuthorizationSequencingCardContent
                authSequenceData={transformAuthSequenceData(variant.authSequenceData)}
              />
            }
            cardFooter={<CardFooter />}
            cardHeader={variant.authSequenceData.product?.nameLong}
            cardHeaderRightSideContent={
              <LoadableChip loadingAllowed={variant.authSequenceData.loadingAllowed} />
            }
            handleEditSequence={() => {}}
            isLoadable={variant.isLoadable}
            key={variant.id}
            title={variant.authSequenceData.product?.nameShort}
          />
        ))}
      </Stack>
    </>
  );
};

export default FilterableCardsPreview;
